/**
 * The one place that answers "how many units of this line should receiving offer?".
 *
 * BLU-1133. A PO line ordered 450 with a packing list saying 0 shipped was
 * offering 450 on "Receive all remaining", so a short shipment received stock
 * that never arrived. The fix is not a per-line null check:
 *
 * D-01 (decision, do not re-attempt the `shipped_quantity != null` branch):
 * `purchase_order_items.shipped_quantity` is declared `INTEGER DEFAULT 0`
 * (20260303000000_add_shipped_quantity.sql) and `createOrder` omits the column
 * on insert, so every freshly created line is 0, never NULL. The backend mapper
 * then flattens any NULL it does find to 0 before the frontend ever sees it.
 * NULL is therefore not a reachable discriminator: branching on it would make
 * the baseline 0 for every line of every PO and "Receive all remaining" would
 * fill nothing anywhere in the app.
 *
 * The reachable discriminator is whether a packing list was recorded FOR THE
 * ORDER, which is an order-level question. `hasPackingList` asks it once and
 * `receivingBaseline` applies the answer per line, so the receive path, the
 * fully-received status check, the row render and the column header cannot
 * drift apart again.
 */

import {
  resolvedUnits,
  unreceivedShippedLines as unreceivedShippedLinesByStamp,
  type UnreceivedResolution,
} from "@threadheads/shared";

/** The shape any purchase order line needs to have a receiving baseline computed. */
export interface ReceivingBaselineItem {
  ordered_quantity: number;
  shipped_quantity?: number | null;
  /** Units of a shipped line resolved as returned or scrapped instead of received. */
  unreceived_quantity?: number | null;
  /** Set once the in-transit remainder of the line has been resolved. */
  unreceived_resolved_at?: string | null;
}

/**
 * True when a packing list has been recorded for this order, i.e. at least one
 * line reports a shipped quantity above zero. See D-01 above: this cannot be
 * answered per line, because an unrecorded line and a genuinely zero line are
 * the same stored 0.
 */
export function hasPackingList(items: readonly ReceivingBaselineItem[]): boolean {
  return items.some((item) => (item.shipped_quantity ?? 0) > 0);
}

/**
 * How many units of this line receiving may take in total: the shipped quantity
 * once a packing list exists for the order, otherwise the ordered quantity.
 *
 * Shipped units resolved as returned or scrapped will never arrive, so they
 * come off the shipped baseline: a fully resolved line reads as nothing left
 * to receive.
 */
export function receivingBaseline(
  item: ReceivingBaselineItem,
  packingListEntered: boolean,
): number {
  return packingListEntered
    ? (item.shipped_quantity ?? 0) - resolvedUnits(item)
    : item.ordered_quantity;
}

/** The order-level fields the short-shipment rules read. */
export interface ShortShipmentOrder {
  /**
   * BLU-535 section D header stamp. Written only when a transfer is shipped
   * with a quantity per SKU. Its presence is what makes `shipped_quantity`
   * literal truth on every line of that order.
   */
  shipped_quantities_recorded_at?: string | null;
}

/** A line that can carry a confirmed shortfall. */
export interface ShortShipmentItem extends ReceivingBaselineItem {
  received_quantity?: number | null;
  unshipped_quantity?: number | null;
  unshipped_confirmed_at?: string | null;
  unreceived_resolution?: UnreceivedResolution | null;
}

/**
 * Whether `shipped_quantity` should be read as the truth on this order.
 *
 * `hasPackingList` alone is not enough for a transfer. A transfer where EVERY
 * line shipped zero has no line above zero, so the packing-list heuristic reads
 * it as "no packing list recorded" and falls back to the ordered quantity,
 * which is the exact opposite of the truth: that order shipped nothing and
 * receiving should offer nothing. The header stamp answers the question
 * directly instead of inferring it, so it is checked first.
 *
 * A purchase order never carries the stamp, so it keeps the packing-list
 * behaviour unchanged.
 */
export function shippedQuantitiesAreAuthoritative(
  order: ShortShipmentOrder,
  items: readonly ReceivingBaselineItem[],
): boolean {
  return Boolean(order.shipped_quantities_recorded_at) || hasPackingList(items);
}

/**
 * The lines that shipped short and whose shortfall nobody has confirmed yet.
 *
 * This mirrors the rule in `purchase-orders.service.ts`
 * (`unconfirmedShortfallLines` there). THE SERVICE COPY IS THE AUTHORITY: it is
 * what actually refuses to complete a transfer, from the complete button, the
 * status dropdown or a direct API call. This copy exists only so a button's
 * enabled state agrees with the server instead of firing and bouncing. Keep the
 * two rules identical.
 *
 * An order with no header stamp returns an empty list, so legacy transfers and
 * every purchase order can still complete exactly as they do today.
 */
export function unconfirmedShortfallLines<T extends ShortShipmentItem>(
  order: ShortShipmentOrder,
  items: readonly T[],
): T[] {
  if (!order.shipped_quantities_recorded_at) return [];
  return items.filter(
    (item) =>
      item.ordered_quantity > (item.shipped_quantity ?? 0) &&
      item.unshipped_confirmed_at == null,
  );
}

/**
 * How many ordered units never left the source on a shipped transfer.
 *
 * Unlike `unconfirmedShortfallLines` this counts every short line, confirmed or
 * not, because it is a record of what happened rather than a queue of work
 * left to do: confirming a shortfall should make it official, not make it
 * vanish from the summary. An order with no header stamp has no shortfall to
 * report, so its ordinary ordered-against-shipped variance keeps being read by
 * the variance panel alone.
 */
export function shortfallUnits(
  order: ShortShipmentOrder,
  items: readonly ReceivingBaselineItem[],
): number {
  if (!order.shipped_quantities_recorded_at) return 0;
  return items.reduce(
    (sum, item) => sum + Math.max(item.ordered_quantity - (item.shipped_quantity ?? 0), 0),
    0,
  );
}

/**
 * The lines of a shipped transfer that still have units in transit: shipped
 * more than the destination has received.
 *
 * The other half of the completion gate, guarding the opposite end from
 * `unconfirmedShortfallLines`. That one is about units that never left the
 * source; this one is about units that DID leave and have not arrived.
 * Completing a transfer while those are outstanding strands them, because the
 * source already decremented them and the destination never will.
 *
 * Units resolved as returned to the source or scrapped count as settled, so a
 * line drops out once received plus resolved covers everything shipped.
 *
 * Runs the shared rule in `@threadheads/shared` (transfer-receipt.ts), the same
 * copy the service's completion gate runs, so the complete button stays
 * disabled exactly when the server would refuse.
 */
export function unreceivedShippedLines<T extends ShortShipmentItem>(
  order: ShortShipmentOrder,
  items: readonly T[],
): T[] {
  return unreceivedShippedLinesByStamp(items, order.shipped_quantities_recorded_at);
}
